<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import {
  columnFilteringFeature,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_includesString,
  globalFilteringFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
  type ColumnDef,
} from '@tanstack/vue-table';
import {useVirtualizer} from '@tanstack/vue-virtual';
import {SvgIcon} from '../svg.ts';
import {parseCsv, serializeCsv, setCsvCell, type CsvTable} from '../utils/csv.ts';
import {
  buildCellIndex, cellBubbleText, cellFlag, cellStatus, columnEditable, columnReadOnly, columnUnit, lookupCell, lookupEngineValue, parseCellNotes,
  type CellFlag, type CellLegendEntry, type CellStatus, type ResolveTarget,
} from '../utils/drcyCellNotes.ts';

// A CSV sheet as a sortable, filterable, virtualized table. Rows are a fixed
// height so the virtualizer never has to measure; derating sheets run to a few
// hundred rows and a couple of dozen columns, and wide sheets scroll inside the
// viewport rather than the page.
const props = withDefaults(defineProps<{
  content: string;
  editable?: boolean;
  // The artifact's `cell_notes` sidecar, if the worker published one; each
  // noted cell shows its entry as a hover bubble.
  cellNotes?: unknown;
  // Whether flagged cells (gaps and proposals the engineer can act on) carry a
  // badge that asks the host to open a resolver for them. Off for read-only
  // previews, which have no host listening.
  interactive?: boolean;
}>(), {editable: false, cellNotes: null, interactive: false});

const emit = defineEmits<{
  'update:content': [content: string];
  'resolve-cell': [target: ResolveTarget];
}>();

// Matches the CSS row height below; both must move together.
const ROW_HEIGHT = 32;
const OVERSCAN = 8;

type CsvRow = {index: number; cells: string[]};

const parsed = computed(() => parseCsv(props.content));
const data = computed<CsvRow[]>(() => parsed.value.rows.map((cells, index) => ({index, cells})));
// Column definitions depend on the header through its serialized form, so a
// cell edit (which re-parses the sheet) does not hand the table new columns.
const headerKey = computed(() => JSON.stringify(parsed.value.header));
const columns = computed<ColumnDef<typeof features, CsvRow>[]>(() => {
  const header = JSON.parse(headerKey.value) as string[];
  return header.map((name, i) => ({
    id: `c${i}`,
    header: name || `Column ${i + 1}`,
    accessorFn: (row: CsvRow) => row.cells[i] ?? '',
    sortFn: 'alphanumeric',
  }));
});

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {alphanumeric: sortFn_alphanumeric},
  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns: {includesString: filterFn_includesString},
});

const table = useTable({
  features,
  columns,
  data,
  getRowId: (row: CsvRow) => String(row.index),
  globalFilterFn: 'includesString',
  getColumnCanGlobalFilter: () => true,
});

const notes = computed(() => parseCellNotes(props.cellNotes));
const cellIndex = computed(() => (notes.value ? buildCellIndex(notes.value, JSON.parse(headerKey.value) as string[]) : null));
// The engine's record columns, which a refresh reads to tell the engineer's
// entries from its own numbers: an edit there would be misread, so they never
// edit. Only an editable table marks them; in a preview nothing edits.
const readOnlyColumns = computed(() => parsed.value.header.map((name) => props.editable && columnReadOnly(notes.value, name)));

function sortTitle(name: string, column: number): string {
  const sort = `Sort by ${name}`;
  return readOnlyColumns.value[column] ? `${sort}. Read-only: DRCY records here what it wrote, so a refresh can tell your entries from its own.` : sort;
}

type FlaggedCell = {flag: CellFlag; entry: CellLegendEntry; status: CellStatus; editable: boolean};
// Every cell the engineer can act on, keyed by source row then column, decided
// once over the whole sheet so the badges, the strip and the row filter cannot
// disagree. Drafts count: a cell they just filled in is no longer flagged.
const flagged = computed(() => {
  const byRow = new Map<number, Map<number, FlaggedCell>>();
  const counts: Record<CellFlag, number> = {needs_input: 0, refused: 0, proposed: 0};
  if (!notes.value || !cellIndex.value) return {byRow, counts};
  const index = cellIndex.value;
  const editableColumns = index.columnFields.map((_, column) => columnEditable(notes.value!, index, column));
  for (const [row, cells] of parsed.value.rows.entries()) {
    for (const column of index.columnFields.keys()) {
      const entry = lookupCell(index, notes.value, cells, column);
      if (!entry) continue;
      const status = cellStatus(entry, cells[column] ?? '', lookupEngineValue(index, notes.value, cells, column));
      const flag = cellFlag(entry, status, editableColumns[column]);
      if (!flag) continue;
      let columns = byRow.get(row);
      if (!columns) byRow.set(row, columns = new Map());
      columns.set(column, {flag, entry, status, editable: editableColumns[column]});
      counts[flag]++;
    }
  }
  return {byRow, counts};
});
const flaggedTotal = computed(() => Object.values(flagged.value.counts).reduce((sum, count) => sum + count, 0));
const flaggedOnly = ref(false);
// The toggle lives in the strip, which leaves with the last flagged cell; the
// filter leaves with it rather than holding an empty view nobody can undo.
watch(flaggedTotal, (total) => {
  if (total === 0) flaggedOnly.value = false;
});

// The global filter goes through the table's row model; "flagged only" is a
// view over that model, so the virtualizer and the pending-edit watch below
// see one list either way.
const rows = computed(() => {
  const all = table.getRowModel().rows;
  return flaggedOnly.value ? all.filter((row) => flagged.value.byRow.has(row.original.index)) : all;
});

function flagOf(row: number, column: number): FlaggedCell | undefined {
  return flagged.value.byRow.get(row)?.get(column);
}

const FLAG_GLYPHS: Record<CellFlag, string> = {needs_input: '?', refused: '!', proposed: '~'};
const FLAG_WORDS: Record<CellFlag, string> = {needs_input: 'needs input', refused: 'refused', proposed: 'proposed'};

// A noted cell shows its bubble through the page's lazy tooltips and drops
// the plain title, which tippy would otherwise copy over the bubble.
function cellAttrs(cells: string[], column: number): Record<string, unknown> {
  const value = cells[column] ?? '';
  if (!notes.value || !cellIndex.value) return {title: value};
  const entry = lookupCell(cellIndex.value, notes.value, cells, column);
  if (!entry) return {title: value};
  const status = cellStatus(entry, value, lookupEngineValue(cellIndex.value, notes.value, cells, column));
  return {
    'data-tooltip-content': cellBubbleText(entry, status),
    class: ['drcy-csv-cell-noted', `drcy-csv-cell-${status.state}`],
  };
}

// The host decides what happens next (a resolver, a chat prefill); the table
// only names the cell and where its badge sits so a panel can anchor to it.
function requestResolve(row: number, column: number, event: MouseEvent) {
  const cell = flagOf(row, column);
  const index = cellIndex.value;
  const anchor = event.currentTarget;
  if (!cell || !index || !notes.value || !(anchor instanceof HTMLElement)) return;
  const cells = parsed.value.rows[row] ?? [];
  emit('resolve-cell', {
    rowIndex: row,
    columnIndex: column,
    referenceColumn: index.referenceColumn,
    reference: cells[index.referenceColumn] ?? '',
    header: parsed.value.header[column] ?? '',
    field: index.columnFields[column],
    unit: columnUnit(notes.value, index, column),
    value: cells[column] ?? '',
    flag: cell.flag,
    entry: cell.entry,
    editable: cell.editable,
    anchor,
  });
}

function badgeLabel(cells: string[], row: number, column: number): string {
  const reference = cells[cellIndex.value?.referenceColumn ?? -1] || `row ${row + 1}`;
  return `Resolve ${columns.value[column]?.header} for ${reference}`;
}

// Column widths come from the whole sheet, not the rows on screen: virtualized
// rows would otherwise resize columns while scrolling, and a cell wider than
// its column would overflow and lose its alignment. Capped so one long note
// cannot push every other column off screen. A column that shows badges also
// reserves a badge's width (1.15rem plus its 0.4rem gap, as styled below).
const COLUMN_MAX_REM = 60;
const columnMinWidths = computed(() => {
  const badged = new Set<number>();
  if (props.interactive) for (const columns of flagged.value.byRow.values()) for (const column of columns.keys()) badged.add(column);
  return parsed.value.header.map((name, i) => {
    let longest = name.length;
    for (const cells of parsed.value.rows) longest = Math.max(longest, cells[i]?.length ?? 0);
    return `min(calc(${longest}ch + ${badged.has(i) ? '2.95rem' : '1.4rem'}), ${COLUMN_MAX_REM}rem)`;
  });
});
const totalRows = computed(() => data.value.length);
const filter = ref('');

watch(filter, (value) => table.setGlobalFilter(value.trim() || undefined));

const viewport = ref<HTMLElement | null>(null);
const virtualizer = useVirtualizer(computed(() => ({
  count: rows.value.length,
  getScrollElement: () => viewport.value,
  estimateSize: () => ROW_HEIGHT,
  getItemKey: (index: number) => rows.value[index]?.id ?? index,
  overscan: OVERSCAN,
})));
const virtualRows = computed(() => virtualizer.value.getVirtualItems());
const paddingTop = computed(() => virtualRows.value[0]?.start ?? 0);
const paddingBottom = computed(() => {
  const last = virtualRows.value.at(-1);
  return last ? virtualizer.value.getTotalSize() - last.end : 0;
});

// An open edit belongs to a cell, not a position: `content` is the sheet its
// row and column were read against, so when the host replaces the sheet under
// it (DRCY republishing) the same cell can be found again.
type EditingCell = {row: number; column: number; content: string};
const editing = ref<EditingCell | null>(null);
const editValue = ref('');
const editInput = ref<HTMLInputElement[]>([]);
// The sheet this table last emitted; the host hands it straight back, and
// that echo moves no cell.
let lastEmitted: string | null = null;

function isEditing(row: number, column: number): boolean {
  return editing.value?.row === row && editing.value?.column === column;
}

async function beginEdit(row: number, column: number) {
  // A click inside the open input bubbles to its cell; do not restart the edit.
  if (!props.editable || readOnlyColumns.value[column] || isEditing(row, column)) return;
  // The open input may have been unmounted by virtual scrolling without a
  // blur, so its value is still pending here.
  commitEdit();
  editing.value = {row, column, content: props.content};
  editValue.value = parsed.value.rows[row]?.[column] ?? '';
  await nextTick();
  const input = editInput.value[0];
  input?.focus();
  input?.select();
}

function cancelEdit() {
  editing.value = null;
}

function commitEdit() {
  const cell = editing.value;
  editing.value = null;
  const target = cell && locateEdit(cell);
  if (!target) return;
  if ((parsed.value.rows[target.row][target.column] ?? '') === editValue.value) return;
  lastEmitted = serializeCsv(setCsvCell(parsed.value, target.row, target.column, editValue.value));
  emit('update:content', lastEmitted);
}

// Where an open edit's cell sits in the current sheet, found by its header and
// its row; null when the cell is gone or its column is the engine's record.
function locateEdit({row, column, content}: EditingCell): {row: number; column: number} | null {
  const now = parsed.value;
  if (content !== props.content) {
    const before = parseCsv(content);
    const header = before.header[column];
    column = now.header[column] === header ? column : now.header.indexOf(header);
    row = relocatedRow(before, now, row);
  }
  if (row < 0 || column < 0 || !now.rows[row] || readOnlyColumns.value[column]) return null;
  return {row, column};
}

// A row is known by its designator where the sidecar names that column and the
// designator is filled in and names one row on both sheets, and by its whole
// text otherwise; -1 when nothing in the current sheet matches.
function relocatedRow(before: CsvTable, now: CsvTable, row: number): number {
  const was = notes.value ? buildCellIndex(notes.value, before.header).referenceColumn : -1;
  const is = cellIndex.value?.referenceColumn ?? -1;
  const designator = before.rows[row]?.[was] ?? '';
  const names = (table: CsvTable, column: number) => table.rows.filter((cells) => cells[column] === designator).length;
  const byDesignator = was >= 0 && is >= 0 && designator !== '' && names(before, was) === 1 && names(now, is) === 1;
  const key = (cells: string[] | undefined, column: number) => (byDesignator ? cells?.[column] : cells && JSON.stringify(cells));
  const wanted = key(before.rows[row], was);
  if (wanted === undefined) return -1;
  if (key(now.rows[row], is) === wanted) return row;
  return now.rows.findIndex((cells) => key(cells, is) === wanted);
}

// The host replaced the sheet under an open edit: the input follows its cell,
// or closes when that cell is gone or now locked.
watch(() => props.content, (content) => {
  const cell = editing.value;
  if (!cell || content === cell.content) return;
  if (content === lastEmitted) {
    editing.value = {...cell, content};
    return;
  }
  const target = locateEdit(cell);
  if (!target) {
    cancelEdit();
    return;
  }
  editing.value = {...target, content};
  if (target.row !== cell.row || target.column !== cell.column) nextTick(() => editInput.value[0]?.focus());
});

onBeforeUnmount(commitEdit);
// Scrolling, sorting or filtering the edited row out of the rendered window
// unmounts its input without a blur, so the edit is committed as the row
// leaves rather than when the engineer next clicks.
watch(virtualRows, (items) => {
  const cell = editing.value;
  if (cell && !items.some((item) => rows.value[item.index]?.original.index === cell.row)) commitEdit();
});

// Enter and Escape belong to the cell while it is being edited. Left to bubble,
// Enter reaches the page's quick-submit handler (which clicks the enclosing
// form's primary button, here "Save") and Escape reaches the workspace's
// close-on-Escape handler.
function onEditKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    commitEdit();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    cancelEdit();
  }
}

// Enter must not submit the enclosing form. Escape clears a filter that has
// text; from an empty box it goes on to the workspace, whose Escape minimizes.
function onFilterKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
  } else if (event.key === 'Escape' && filter.value !== '') {
    event.preventDefault();
    event.stopPropagation();
    filter.value = '';
  }
}

function sortLabel(direction: 'asc' | 'desc' | false): 'ascending' | 'descending' | 'none' {
  if (direction === 'asc') return 'ascending';
  if (direction === 'desc') return 'descending';
  return 'none';
}
</script>

<template>
  <div class="drcy-csv-table" :class="{'drcy-csv-table-editable': editable}">
    <div class="drcy-csv-toolbar">
      <div class="ui mini icon input drcy-csv-filter">
        <input v-model="filter" type="search" placeholder="Filter rows" aria-label="Filter rows" @keydown="onFilterKeydown">
        <i class="search icon"></i>
      </div>
      <span class="drcy-csv-count tw:text-text-light-2 tw:text-sm" aria-live="polite">
        <template v-if="filter.trim() || flaggedOnly">{{ rows.length }} of {{ totalRows }} rows</template>
        <template v-else>{{ totalRows }} {{ totalRows === 1 ? 'row' : 'rows' }}</template>
      </span>
      <span v-if="interactive && flaggedTotal > 0" class="drcy-csv-flags tw:text-sm" role="group" aria-label="Flagged cells">
        <span class="drcy-csv-flag-counts tw:text-text-light-2">
          <template v-for="(count, flag, position) in flagged.counts" :key="flag">
            <template v-if="count > 0"><span v-if="position > 0 && Object.values(flagged.counts).slice(0, position).some((n) => n > 0)"> · </span>{{ count }} {{ FLAG_WORDS[flag] }}</template>
          </template>
        </span>
        <button
          type="button"
          class="ui tiny basic button drcy-csv-flag-toggle"
          :class="{active: flaggedOnly}"
          :aria-pressed="flaggedOnly ? 'true' : 'false'"
          @click="flaggedOnly = !flaggedOnly"
        >
          Flagged only
        </button>
      </span>
      <span v-if="editable" class="drcy-csv-hint tw:text-text-light-2 tw:text-sm">Click a cell to edit it</span>
    </div>
    <div ref="viewport" class="drcy-csv-viewport" tabindex="0">
      <table class="ui very compact small celled unstackable table drcy-csv-grid">
        <thead>
          <tr v-for="group in table.getHeaderGroups()" :key="group.id">
            <th
              v-for="(header, columnIndex) in group.headers"
              :key="header.id"
              scope="col"
              :style="{minWidth: columnMinWidths[columnIndex]}"
              :aria-sort="sortLabel(header.column.getIsSorted())"
            >
              <button
                type="button"
                class="drcy-csv-sort"
                :title="sortTitle(String(header.column.columnDef.header), columnIndex)"
                @click="header.column.toggleSorting()"
              >
                <SvgIcon v-if="readOnlyColumns[columnIndex]" name="octicon-lock" :size="12" class="drcy-csv-lock"/>
                <span class="drcy-csv-sort-label">{{ header.column.columnDef.header }}</span>
                <span v-if="readOnlyColumns[columnIndex]" class="tw:sr-only">(read-only)</span>
                <span class="drcy-csv-sort-mark" aria-hidden="true">
                  {{ header.column.getIsSorted() === 'asc' ? '▲' : header.column.getIsSorted() === 'desc' ? '▼' : '' }}
                </span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paddingTop > 0" class="drcy-csv-spacer" aria-hidden="true">
            <td :colspan="columns.length" :style="{height: `${paddingTop}px`}"></td>
          </tr>
          <tr
            v-for="item in virtualRows"
            :key="String(item.key)"
            class="drcy-csv-row"
            :data-row="rows[item.index].original.index"
          >
            <td
              v-for="(_column, columnIndex) in columns"
              :key="columnIndex"
              class="drcy-csv-cell"
              :class="{
                'drcy-csv-cell-editing': isEditing(rows[item.index].original.index, columnIndex),
                'drcy-csv-cell-readonly': readOnlyColumns[columnIndex],
              }"
              v-bind="cellAttrs(rows[item.index].original.cells, columnIndex)"
              @click="beginEdit(rows[item.index].original.index, columnIndex)"
            >
              <input
                v-if="isEditing(rows[item.index].original.index, columnIndex)"
                ref="editInput"
                v-model="editValue"
                class="drcy-csv-cell-input"
                type="text"
                :aria-label="`${columns[columnIndex]?.header} for row ${rows[item.index].original.index + 1}`"
                @keydown="onEditKeydown"
                @blur="commitEdit"
              >
              <template v-else>
                <button
                  v-if="interactive && flagOf(rows[item.index].original.index, columnIndex)"
                  type="button"
                  class="drcy-csv-cell-flag"
                  :class="`drcy-csv-cell-flag-${flagOf(rows[item.index].original.index, columnIndex)!.flag}`"
                  :aria-label="badgeLabel(rows[item.index].original.cells, rows[item.index].original.index, columnIndex)"
                  @click.stop="requestResolve(rows[item.index].original.index, columnIndex, $event)"
                >{{ FLAG_GLYPHS[flagOf(rows[item.index].original.index, columnIndex)!.flag] }}</button>
                {{ rows[item.index].original.cells[columnIndex] ?? '' }}
              </template>
            </td>
          </tr>
          <tr v-if="paddingBottom > 0" class="drcy-csv-spacer" aria-hidden="true">
            <td :colspan="columns.length" :style="{height: `${paddingBottom}px`}"></td>
          </tr>
          <tr v-if="rows.length === 0" class="drcy-csv-empty">
            <td :colspan="Math.max(columns.length, 1)">{{ totalRows === 0 ? 'This sheet has no rows.' : 'No rows match the filter.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.drcy-csv-table {
  /* One switch for how every cell aligns; numbers and text share it. */
  --drcy-csv-cell-align: right;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
}
.drcy-csv-toolbar {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}
.drcy-csv-filter {
  flex: 0 1 16rem;
  min-width: 8rem;
}
.drcy-csv-hint {
  margin-left: auto;
}
.drcy-csv-flags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.6rem;
  align-items: center;
}
.drcy-csv-flags > .button {
  margin: 0 !important;
}
/* The badge that opens a flagged cell's resolver; sits before the value so a
   right-aligned number keeps its column. */
.drcy-csv-cell-flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  margin-right: 0.4rem;
  padding: 0;
  color: var(--color-text-dark);
  font: inherit;
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  vertical-align: middle;
  background: var(--color-box-header);
  border: 1px solid var(--color-secondary);
  border-radius: 50%;
  cursor: pointer;
}
.drcy-csv-cell-flag:hover {
  background: var(--color-hover);
}
.drcy-csv-cell-flag:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 1px;
}
.drcy-csv-cell-flag-needs_input {
  border-color: var(--color-yellow);
}
.drcy-csv-cell-flag-refused {
  border-color: var(--color-red);
}
.drcy-csv-cell-flag-proposed {
  border-color: var(--color-blue);
}
.drcy-csv-viewport {
  position: relative;
  flex: 1 1 auto;
  /* Never fewer than seven 32px rows (the root font is 14px), even when the
     workspace is crowded; the list around the table scrolls instead. */
  min-height: 16rem;
  /* Hosts that are not flex-bounded (a file row) cap the viewport through this. */
  max-height: var(--drcy-csv-max-height, none);
  overflow: auto;
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.drcy-csv-grid.ui.table {
  width: max-content;
  min-width: 100%;
  margin: 0;
  border: 0;
  border-radius: 0;
  table-layout: auto;
}
.drcy-csv-grid thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 0 !important;
  background: var(--color-box-header);
  box-shadow: inset 0 -1px 0 var(--color-secondary);
}
.drcy-csv-sort {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 0.4rem 0.6rem;
  color: inherit;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  text-align: var(--drcy-csv-cell-align);
  white-space: nowrap;
  background: none;
  border: 0;
  cursor: pointer;
}
.drcy-csv-sort:hover {
  background: var(--color-hover);
}
.drcy-csv-sort-mark {
  flex: 0 0 0.75rem;
  font-size: 0.7em;
}
.drcy-csv-grid td.drcy-csv-cell {
  height: 32px;
  max-width: 60rem;
  padding: 0 0.6rem !important;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
  line-height: 32px;
  text-align: var(--drcy-csv-cell-align);
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}
/* A noted cell carries a bubble; the bar says where to hover. */
.drcy-csv-grid td.drcy-csv-cell-noted {
  box-shadow: inset 3px 0 0 var(--color-text-light-3);
}
.drcy-csv-grid td.drcy-csv-cell-needs_input {
  box-shadow: inset 3px 0 0 var(--color-yellow);
}
.drcy-csv-grid td.drcy-csv-cell-refused {
  box-shadow: inset 3px 0 0 var(--color-red);
}
.drcy-csv-grid td.drcy-csv-cell-proposed {
  box-shadow: inset 3px 0 0 var(--color-blue);
}
.drcy-csv-grid td.drcy-csv-cell-edited {
  box-shadow: inset 3px 0 0 var(--color-green);
}
.drcy-csv-table-editable td.drcy-csv-cell:not(.drcy-csv-cell-readonly) {
  cursor: text;
}
.drcy-csv-table-editable td.drcy-csv-cell:not(.drcy-csv-cell-readonly):hover {
  background: var(--color-hover);
}
/* The engine's record columns: shown for reference, never edited. */
.drcy-csv-grid td.drcy-csv-cell-readonly {
  color: var(--color-text-light-2);
  cursor: default;
}
.drcy-csv-lock {
  flex: 0 0 auto;
  color: var(--color-text-light-2);
}
.drcy-csv-grid td.drcy-csv-cell-editing {
  padding: 0 !important;
}
.drcy-csv-cell-input {
  box-sizing: border-box;
  width: 100%;
  height: 32px;
  padding: 0 0.6rem;
  color: inherit;
  font: inherit;
  text-align: var(--drcy-csv-cell-align);
  background: var(--color-input-background);
  border: 0;
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}
.drcy-csv-grid .drcy-csv-spacer td {
  padding: 0 !important;
  border: 0;
}
.drcy-csv-grid .drcy-csv-empty td {
  color: var(--color-text-light-2);
  text-align: center;
}
</style>
